import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, Sparkles, Wrench, Car, Hotel as HotelIcon,
  Package, ArrowRight, Check, Award, Fuel, ShieldCheck, Coins, CircleDot, Droplet, BellRing,
} from 'lucide-react';

/**
 * FuelXCardBenefits — Vorteils-Sektion für die FuelX Card.
 *
 * Wir kommunizieren ZWEI getrennte Reward-Systeme:
 *
 * 1) TANKGUTHABEN (Wallet) — wird mit der Card-Bestellung im User-Dashboard
 *    gutgeschrieben. Einlösbar für Hotelbuchungen, Auto-Verkauf,
 *    Reparatur und Ersatzteile.
 *
 * 2) PREPAID-TANKKARTE — wird von FuelX bei Hotelbuchungen mit Guthaben befüllt.
 *    Kostenloses Tanken, kostenloser TÜV/HU, Öl- & Reifenwechsel —
 *    FuelGood Guthaben pro FuelX-Hotelbuchung.
 *
 * Varianten:
 *   - "section"  — full-width Section für Landing Pages
 *   - "compact"  — kompaktes Grid (für CardConfigurator-Nähe)
 *   - "list"     — pure Liste (für Sidebars / Drawer)
 */

export const TANKGUTHABEN_BENEFITS = [
  { icon: HotelIcon, title: 'FuelGood Guthaben pro Hotelbuchung einlösen', text: 'Jede qualifizierte Buchung verrechnet FuelGood Guthaben aus deinem FuelGood Guthaben — direkt mit dem Hotelpreis.' },
  { icon: Award, title: 'Bestpreis auf jedes Hotel', text: 'FUEL.X Bestpreis-Garantie bei qualifizierten Hotelbuchungen.' },
  { icon: Fuel, title: 'Ø 0,40 €/Liter günstiger tanken', text: 'Dein FuelGood Guthaben senkt deine effektiven Spritkosten pro Liter um durchschnittlich 0,40 €.' },
  { icon: Coins, title: 'KFZ-Steuer mit Guthaben decken', text: 'Auch deine KFZ-Steuer kannst du aus deinem FuelGood Guthaben bezahlen.' },
];

export const PREPAID_TANKCARD_BENEFITS = [
  { icon: ShieldCheck, title: 'TÜV/HU: bis zu 100 % mit Guthaben bezahlen', text: 'Dein FuelGood Guthaben übernimmt die Rechnung — je nach Guthaben-Stand bis zur vollen Höhe. Wir organisieren den Termin.' },
  { icon: CircleDot, title: 'Reifenwechsel: bis zu 100 % mit Guthaben', text: 'Saisonaler Wechsel inkl. Einlagerung bei geprüften Dienstleistern — bezahlt aus deinem Guthaben.' },
  { icon: Droplet, title: 'Ölwechsel: bis zu 100 % mit Guthaben', text: 'Original-Öl bei 2.400+ Werkstätten — dein Guthaben deckt bis zur vollen Rechnung.' },
  { icon: Wrench, title: 'Werkstatt-Bestpreis ohne Abzocke', text: 'Nur geprüfte Dienstleister, transparente Leistungen — keine unnötigen Services.' },
  { icon: BellRing, title: 'Service-Erinnerungen automatisch', text: 'TÜV, Öl, Reifen, Service — dein Account meldet sich rechtzeitig.' },
];

export default function FuelXCardBenefits({ variant = 'section', testIdPrefix = 'card-benefits' }) {
  if (variant === 'list') return <BenefitList testIdPrefix={testIdPrefix} />;
  if (variant === 'compact') return <BenefitCompact testIdPrefix={testIdPrefix} />;
  return <BenefitSection testIdPrefix={testIdPrefix} />;
}

// ─── Variante: Full-Width Section (Landing-Page) ─────────────────────────────
function BenefitSection({ testIdPrefix }) {
  return (
    <section className="py-20 sm:py-28 bg-stone-950 text-white relative overflow-hidden" data-testid={`${testIdPrefix}-section`}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(62,255,126,0.10),_transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-300 mb-4">FuelX Card · Mehr als nur Tanken</p>
          <h2 className="font-display leading-[0.95] tracking-[-0.028em] text-[2.5rem] sm:text-[3.25rem] lg:text-[4rem] mb-5">
            Zwei Reward-Systeme. <br />
            <span className="italic font-light text-emerald-300">Eine Card.</span>
          </h2>
          <p className="text-base sm:text-lg text-stone-300 leading-relaxed max-w-2xl">
            Mit deiner FuelX Card erhältst du <strong className="text-white">zwei getrennte Guthaben-Töpfe</strong>: dein FuelGood Guthaben für Auto-Services und Hotels — plus eine Prepaid-Tankkarte, die FuelX bei jeder Hotelbuchung für dich auflädt.
          </p>
        </div>

        {/* ═══ Topf 1: FuelGood Guthaben ═══ */}
        <BenefitColumn
          eyebrow="Reward-System #1"
          title="FuelGood Guthaben"
          subtitle="Wird mit der Card-Bestellung in deinem Dashboard gutgeschrieben — einlösbar für Hotels, Auto-Verkauf, Reparaturen und Ersatzteile."
          items={TANKGUTHABEN_BENEFITS}
          testIdPrefix={`${testIdPrefix}-tank`}
        />

        {/* ═══ Topf 2: Prepaid-Tankkarte ═══ */}
        <div className="mt-16">
          <BenefitColumn
            eyebrow="Reward-System #2"
            title="Prepaid-Tankkarte"
            subtitle="Wird von FuelX bei jeder Hotelbuchung mit FuelGood Guthaben befüllt — damit zahlst du Tanken, TÜV/HU sowie Öl- und Reifenwechsel direkt aus deinem Guthaben."
            items={PREPAID_TANKCARD_BENEFITS}
            testIdPrefix={`${testIdPrefix}-prepaid`}
          />
        </div>

        {/* Bestpreis-Garantie + No-Hassle-Banner */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 sm:p-7 flex items-start gap-4">
            <Award className="w-7 h-7 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <h4 className="font-display text-xl text-white mb-1.5 tracking-[-0.01em]">Bestpreis-Garantie für alle Dienstleistungen</h4>
              <p className="text-sm text-stone-300 leading-relaxed">
                Findest du selbst einen günstigeren Anbieter, übernehmen wir die Differenz — bis zu 200 € pro Service.
              </p>
            </div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 sm:p-7 flex items-start gap-4">
            <Sparkles className="w-7 h-7 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <h4 className="font-display text-xl text-white mb-1.5 tracking-[-0.01em]">Keine Recherche. Kein Zeitaufwand.</h4>
              <p className="text-sm text-stone-300 leading-relaxed">
                Du sagst uns, was du brauchst — wir erledigen den Rest. Termine, Verhandlungen, Verträge, Abwicklung.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/silver-card" className="inline-flex items-center gap-2 bg-white text-stone-900 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-emerald-300 transition-colors" data-testid={`${testIdPrefix}-cta`}>
            FuelX Card bestellen <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
          <Link to="/preise" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors">
            Preise ansehen
          </Link>
        </div>
      </div>
    </section>
  );
}

function BenefitColumn({ eyebrow, title, subtitle, items, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-block`}>
      <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-12 mb-7">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-300/80 mb-3">{eyebrow}</p>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-[-0.025em] leading-[1.05]">{title}</h3>
        </div>
        <p className="text-sm sm:text-base text-stone-400 leading-relaxed self-end pb-2">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((b, i) => {
          const Icon = b.icon;
          return (
            <div
              key={i}
              className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 backdrop-blur-sm hover:border-emerald-500/40 hover:bg-white/[0.06] transition-all"
              data-testid={`${testIdPrefix}-item-${i}`}
            >
              <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                <Icon className="w-4 h-4 text-emerald-300" strokeWidth={2} />
              </div>
              <h4 className="font-display text-lg text-white mb-1.5 leading-tight tracking-[-0.01em]">{b.title}</h4>
              <p className="text-xs text-stone-300 leading-relaxed">{b.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Variante: Kompakt (CardConfigurator + Drawer) ───────────────────────────
function BenefitCompact({ testIdPrefix }) {
  return (
    <div className="space-y-6" data-testid={`${testIdPrefix}-compact`}>
      <CompactGroup
        eyebrow="Reward #1 · FuelGood Guthaben"
        subtitle="Wird mit Card-Bestellung gutgeschrieben"
        items={TANKGUTHABEN_BENEFITS}
        testIdPrefix={`${testIdPrefix}-tank`}
        accent="emerald"
      />
      <CompactGroup
        eyebrow="Reward #2 · Prepaid-Tankkarte"
        subtitle="Wird bei jeder FuelX-Hotelbuchung von uns aufgeladen"
        items={PREPAID_TANKCARD_BENEFITS}
        testIdPrefix={`${testIdPrefix}-prepaid`}
        accent="amber"
      />
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
        <Award className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
        <p className="text-xs text-stone-700 leading-relaxed">
          <strong className="text-stone-900">Bestpreis-Garantie</strong> für alle Dienstleistungen — keine Recherche, kein Zeitaufwand. Wir erledigen alles.
        </p>
      </div>
    </div>
  );
}

function CompactGroup({ eyebrow, subtitle, items, testIdPrefix, accent }) {
  const accentMap = {
    emerald: { dot: 'bg-emerald-500', label: 'text-emerald-700' },
    amber: { dot: 'bg-amber-500', label: 'text-amber-700' },
  };
  const a = accentMap[accent] || accentMap.emerald;
  return (
    <div data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${a.dot}`} />
        <p className={`text-[10px] font-medium uppercase tracking-[0.22em] ${a.label}`}>{eyebrow}</p>
        <span className="text-[10px] text-stone-400">— {subtitle}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((b, i) => {
          const Icon = b.icon;
          return (
            <div key={i} className="bg-white border border-stone-200 rounded-xl p-3 flex items-start gap-3 hover:border-stone-300 transition-colors" data-testid={`${testIdPrefix}-item-${i}`}>
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-stone-700" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-stone-900 leading-tight">{b.title}</p>
                <p className="text-[10px] text-stone-500 leading-relaxed mt-0.5">{b.text}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Variante: Liste (für Drawer/Sidebar) ────────────────────────────────────
function BenefitList({ testIdPrefix }) {
  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-list`}>
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-700">Reward #1 · FuelGood Guthaben</p>
        </div>
        <ul className="space-y-1.5">
          {TANKGUTHABEN_BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <li key={i} className="flex items-start gap-2 text-xs text-stone-700" data-testid={`${testIdPrefix}-list-tank-item-${i}`}>
                <Icon className="w-3 h-3 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                <div><strong className="text-stone-900">{b.title}</strong> <span className="text-stone-500">— {b.text.split('—')[0].trim().replace(/\.$/, '')}</span></div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="pt-2 border-t border-stone-100">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-amber-700">Reward #2 · Prepaid-Tankkarte (bei Hotelbuchung)</p>
        </div>
        <ul className="space-y-1.5">
          {PREPAID_TANKCARD_BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <li key={i} className="flex items-start gap-2 text-xs text-stone-700" data-testid={`${testIdPrefix}-list-prepaid-item-${i}`}>
                <Icon className="w-3 h-3 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                <div><strong className="text-stone-900">{b.title}</strong> <span className="text-stone-500">— {b.text.split('—')[0].trim().replace(/\.$/, '')}</span></div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="pt-2 border-t border-stone-100 flex items-start gap-2 text-xs text-emerald-800">
        <Award className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
        <span><strong className="text-stone-900">Bestpreis-Garantie</strong> — keine Recherche, kein Zeitaufwand.</span>
      </div>
    </div>
  );
}
